import { get, post } from '@/services/http';

// Times are exchanged with the backend as UTC strings in this format.
export const ETL_STATUS_DATE_FORMAT = 'YYYY/MM/DD HH:mm:ss';

export interface EtlStatusRow {
  id: number;
  merchantId: number;
  resource: string;
  etlType: string;
  status: string | null;
  limitStartTime: string | null;
  startTime: string | null;
  endTime: string | null;
  pendingStartTime: string | null;
  pendingEndTime: string | null;
  updatedAt: string | null;
}

export interface ReEtlResult {
  etlStatus: EtlStatusRow;
  triggered: boolean;
  triggerError: string | null;
}

const DATE_PATTERN = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

// Strict check: right shape and a real calendar date/time (rejects 2026/02/30, 25:00:00, ...).
export function isValidEtlStatusDate(value: string): boolean {
  const match = DATE_PATTERN.exec(value);
  if (!match) {
    return false;
  }
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
    && date.getUTCHours() === hour
    && date.getUTCMinutes() === minute
    && date.getUTCSeconds() === second;
}

// Mirrors the backend checks. Values share one fixed-width format, so string order is time order.
export function validateReEtlFromTime(row: EtlStatusRow, value: string): true | string {
  if (!row.pendingStartTime || !row.pendingEndTime) {
    return 'No incremental watermark yet; the next run is a full historical load';
  }
  const trimmed = (value || '').trim();
  if (!isValidEtlStatusDate(trimmed)) {
    return `Must be ${ETL_STATUS_DATE_FORMAT}`;
  }
  if (trimmed >= row.pendingEndTime) {
    return `Must be before the current watermark ${row.pendingEndTime}`;
  }
  if (row.limitStartTime && trimmed < row.limitStartTime) {
    return `Cannot be before the history limit ${row.limitStartTime}`;
  }
  return true;
}

export async function fetchEtlStatuses(storeId: number): Promise<EtlStatusRow[]> {
  const res: any = await get(`/stores/${storeId}/etl_statuses`);
  return (res && res.data) || [];
}

export async function reEtlResource(
  storeId: number,
  etlStatusId: number,
  fromTime: string,
  triggerNow: boolean,
): Promise<ReEtlResult> {
  const res: any = await post(`/stores/${storeId}/etl_statuses/${etlStatusId}/re_etl`, { fromTime, triggerNow });
  return res.data;
}
